// Styled Excel export: coloured header, wrapped text, borders, zebra rows, auto widths, frozen header, filters.
// Needs:  npm install xlsx-js-style   (drop-in superset of "xlsx" that can write cell styles)
import * as XLSX from 'xlsx-js-style';

const HEADER_FILL = '1F3A5F';   // dark blue
const ZEBRA_FILL = 'F3F6FB';
const TOTAL_FILL = 'DCE6F5';
const BORDER = { style: 'thin', color: { rgb: 'C5CEDC' } };
const BORDERS = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
const MONEY_RE = /fee|paid|balance|amount|total|overdue|discount|fine|collected|due|cash|income|expense|net|opening|closing|credit|debit|payable|receivable/i;

function colWidth(header, values) {
  let w = String(header).length + 2;
  for (const v of values) {
    if (v == null) continue;
    const longestLine = String(v).split('\n').reduce((m, l) => Math.max(m, l.length), 0);
    w = Math.max(w, Math.min(longestLine + 2, 45));
  }
  return Math.max(8, Math.min(w, 45));
}

// sheets: [{ name, rows:[{col:value}], title?:string, widths?:{col:wch} }]
export function buildStyledSheet({ rows, title, widths = {} }) {
  const headers = rows.length ? Object.keys(rows[0]) : [];
  const offset = title ? 2 : 0;
  const ws = XLSX.utils.json_to_sheet(rows, { origin: { r: offset, c: 0 } });
  if (title) {
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A1' });
    ws['A1'].s = { font: { bold: true, sz: 14, color: { rgb: HEADER_FILL } }, alignment: { vertical: 'center' } };
    (ws['!merges'] = ws['!merges'] || []).push({ s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(headers.length - 1, 0) } });
  }
  const moneyCols = new Set(headers.filter(h => MONEY_RE.test(h) && rows.some(r => typeof r[h] === 'number')));
  const numCols = new Set(headers.filter(h => rows.length && rows.every(r => r[h] === '' || r[h] == null || typeof r[h] === 'number') && rows.some(r => typeof r[h] === 'number')));
  const decimalCols = new Set(headers.filter(h => rows.some(r => typeof r[h] === 'number' && !Number.isInteger(r[h]))));
  headers.forEach((h, c) => {
    const ref = XLSX.utils.encode_cell({ r: offset, c });
    ws[ref].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
      fill: { patternType: 'solid', fgColor: { rgb: HEADER_FILL } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: BORDERS,
    };
  });
  rows.forEach((row, r) => {
    const isTotal = Object.values(row).some(v => typeof v === 'string' && /^total\b/i.test(v.trim()));
    headers.forEach((h, c) => {
      const ref = XLSX.utils.encode_cell({ r: r + offset + 1, c });
      let cell = ws[ref];
      if (!cell) { cell = ws[ref] = { t: 's', v: '' }; }
      const isNum = typeof cell.v === 'number';
      if (isNum && moneyCols.has(h)) cell.z = decimalCols.has(h) ? '#,##0.00' : '#,##0';
      cell.s = {
        font: { sz: 10, bold: isTotal },
        fill: isTotal ? { patternType: 'solid', fgColor: { rgb: TOTAL_FILL } } : (r % 2 === 1 ? { patternType: 'solid', fgColor: { rgb: ZEBRA_FILL } } : undefined),
        alignment: { vertical: 'top', wrapText: true, horizontal: isNum || numCols.has(h) ? (moneyCols.has(h) ? 'right' : 'center') : 'left' },
        border: BORDERS,
      };
    });
  });
  ws['!cols'] = headers.map(h => ({ wch: widths[h] || colWidth(h, rows.map(r => r[h])) }));
  ws['!rows'] = [];
  ws['!rows'][offset] = { hpt: 30 };
  if (title) ws['!rows'][0] = { hpt: 24 };
  ws['!freeze'] = { xSplit: 0, ySplit: offset + 1 };
  ws['!views'] = [{ state: 'frozen', ySplit: offset + 1 }];
  if (headers.length) ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: offset, c: 0 }, e: { r: offset + rows.length, c: headers.length - 1 } }) };
  ws['!pageSetup'] = { orientation: 'landscape', fitToWidth: 1, fitToHeight: 0 };
  ws['!sheetPr'] = { pageSetUpPr: { fitToPage: true } };
  return ws;
}

export function saveStyledXlsx(filename, sheets) {
  const wb = XLSX.utils.book_new();
  const used = new Set();
  sheets.forEach((sh, i) => {
    let name = String(sh.name || 'Sheet' + (i + 1)).replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Sheet' + (i + 1);
    let n = 2; const base = name;
    while (used.has(name.toLowerCase())) name = base.slice(0, 28) + ' ' + n++;
    used.add(name.toLowerCase());
    XLSX.utils.book_append_sheet(wb, buildStyledSheet(sh), name);
  });
  XLSX.writeFile(wb, filename.replace(/[\\/:*?"<>|]/g, '_'));
}
